import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { firmDocumentsUrl } from 'common/config/urls';
import { PROCESS_MANAGEMENT_ENABLER } from 'common/constants/features';
import { DATE_ALIAS } from 'common/constants/general';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import { useSecondaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import FirmRequests from 'pages/Firms/Requests/FirmRequests';
import FirmDocuments from './FirmDocuments';
import {
  FIRM_DOCUMENTS_WORKSPACE_DEFAULT_TAB,
  FIRM_DOCUMENTS_WORKSPACE_LABELS,
  FIRM_DOCUMENTS_WORKSPACE_TAB_SLUGS,
} from './FirmDocumentsWorkspace.constants';

const getDocumentsSectionSearch = (search: string) => {
  const currentSearchParams = new URLSearchParams(search);
  const nextSearchParams = new URLSearchParams();
  const date = currentSearchParams.get(DATE_ALIAS);

  if (date) {
    nextSearchParams.set(DATE_ALIAS, date);
  }

  const queryString = nextSearchParams.toString();

  return queryString ? `?${queryString}` : '';
};

const getDocumentsTabSearch = (baseSearch: string, documentsSearch: string) => {
  const nextSearchParams = new URLSearchParams(baseSearch);

  new URLSearchParams(documentsSearch).forEach((value, key) => {
    if (key !== DATE_ALIAS) {
      nextSearchParams.set(key, value);
    }
  });

  const queryString = nextSearchParams.toString();

  return queryString ? `?${queryString}` : '';
};

const FirmDocumentsWorkspace = () => {
  const { documentsTabSlugParam, firmSlugParam } = useParams<{
    documentsTabSlugParam?: string;
    firmSlugParam: string;
  }>();
  const location = useLocation();
  const [, checkFeature] = useCheckFeatures();
  const isProcessManagementEnabled = checkFeature(PROCESS_MANAGEMENT_ENABLER);
  const activeTab = documentsTabSlugParam ?? FIRM_DOCUMENTS_WORKSPACE_DEFAULT_TAB;
  const baseSearch = getDocumentsSectionSearch(location.search);
  const [documentsTabSearch, setDocumentsTabSearch] = useState(location.search);
  const documentsRootPath = firmSlugParam ? firmDocumentsUrl(firmSlugParam) : undefined;
  const documentsTabTo = documentsRootPath
    ? `${documentsRootPath}${getDocumentsTabSearch(baseSearch, documentsTabSearch)}`
    : undefined;

  // Preserve Documents-only search/filter params while sibling tabs use only shared workspace params.
  useEffect(() => {
    if (activeTab === FIRM_DOCUMENTS_WORKSPACE_DEFAULT_TAB) {
      setDocumentsTabSearch(location.search);
    }
  }, [activeTab, location.search]);

  const secondaryNavigation = useMemo(
    () => ({
      ariaLabel: FIRM_DOCUMENTS_WORKSPACE_LABELS.secondaryNavigationAriaLabel,
      items: [
        {
          label: FIRM_DOCUMENTS_WORKSPACE_LABELS.documents,
          to: documentsTabTo,
          value: FIRM_DOCUMENTS_WORKSPACE_DEFAULT_TAB,
        },
        ...(isProcessManagementEnabled
          ? [
            {
              label: FIRM_DOCUMENTS_WORKSPACE_LABELS.requests,
              to: documentsRootPath
                ? `${documentsRootPath}/${FIRM_DOCUMENTS_WORKSPACE_TAB_SLUGS.REQUESTS}${baseSearch}`
                : undefined,
              value: FIRM_DOCUMENTS_WORKSPACE_TAB_SLUGS.REQUESTS,
            },
          ]
          : []),
      ],
      title: FIRM_DOCUMENTS_WORKSPACE_LABELS.documents,
      value: activeTab,
    }),
    [activeTab, baseSearch, documentsRootPath, documentsTabTo, isProcessManagementEnabled]
  );

  useSecondaryNavigationRegistration(secondaryNavigation);

  if (!firmSlugParam || !documentsRootPath) {
    return null;
  }

  if (documentsTabSlugParam === FIRM_DOCUMENTS_WORKSPACE_DEFAULT_TAB) {
    return (
      <Navigate
        to={`${documentsRootPath}${location.search}`}
        replace
      />
    );
  }

  if (activeTab === FIRM_DOCUMENTS_WORKSPACE_TAB_SLUGS.REQUESTS && !isProcessManagementEnabled) {
    return (
      <Navigate
        to={`${documentsRootPath}${baseSearch}`}
        replace
      />
    );
  }

  switch (activeTab) {
    case FIRM_DOCUMENTS_WORKSPACE_DEFAULT_TAB:
      return <FirmDocuments />;
    case FIRM_DOCUMENTS_WORKSPACE_TAB_SLUGS.REQUESTS:
      return <FirmRequests />;
    default:
      return (
        <Navigate
          to={`${documentsRootPath}${location.search}`}
          replace
        />
      );
  }
};

export default FirmDocumentsWorkspace;
